/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable unicorn/no-null */
import { StateCreator } from 'zustand';

import logger from '@/logger';
import { MissionLo } from '@/pages/MissionSelection/components/MissionList';
import { teardownNudging } from '@/pages/Nudging/nudgingEngine';
import { Stage, StudentInfoResponse } from '@/services/api';
import { resolveInitialTheme, ThemeName } from '@/theme/themes';
import {
  COIN_BALANCE_SESSION_KEY,
  getSessionItem,
  removeSessionItem,
  setSessionItem,
} from '@/utils/helper';

export type Answer = string[] | { id: string; text?: string; image?: string }[];

export type QuestionStatus = {
  status: string;
  index: number;
  answer: Answer;
};

export type Student = {
  grade: string;
  name: string;
  id: string;
  avatar?: string | null;
};

export type DesktopConfig = {
  allowOnlineStreaming: boolean;
  offlineStreamingAvailable: boolean;
};

export interface AppState {
  currentTheme: ThemeName;
  loaderCount: number;
  questionStatusArray: QuestionStatus[] | null;
  isInternetIssue: boolean;
  currentLevel: string | null;
  currentMission: MissionLo | undefined;
  currentStage: Stage | undefined;
  serverTime?: string;
  performanceReference?: number;
  currentStudent: Student | undefined;
  performanceTimeoriginReference?: number;
  logoutSource?: string;
  moduleCompletionThreshold: number | undefined;
  lastActiveMission: MissionLo | undefined;
  desktopConfig: DesktopConfig | null;
  appVersion: string;
  remoteConfigObject: Record<string, string>;
  fcmToken: string | undefined;
  callGetServerTime: boolean;
  isUserInitialized: boolean;
  stageList: Stage[] | null;
  // isNarrationActive: boolean;
  appLanguage: string;
  currentAdminSession: { sessionId: string; adminId: string } | undefined;
  hasLeveledUp: boolean;
  hasLeveledDown: boolean;
  hasCompletedBaseline: boolean;
  studentInfo: StudentInfoResponse | undefined;
  showMissionUnlockDialog: boolean;
  showMonsterCardDialog: boolean;
  activityEnabled: boolean | undefined;
  coinsAvailable: boolean | undefined;
  nudgesEnabled: boolean | undefined;
  showSkillsCard: boolean;
  pendingCoinAnimation: number;
  isNewStudent: boolean | undefined;
  setIsNewStudent: (isNewStudent: boolean | undefined) => void;
  setCurrentTheme: (theme: ThemeName) => void;
  setShowLoader: (showLoader: boolean) => void;
  setQuestionStatusArray: (questionStatusArray: QuestionStatus[] | null) => void;
  setInternetIssue: (isInternetIssue: boolean) => void;
  setCurrentLevel: (level: string) => void;
  setCurrentMission: (mission: MissionLo | undefined) => void;
  setCurrentStage: (stage: Stage | undefined) => void;
  setServerTime: (serverTime: string) => void;
  getCurrentTime: () => number;
  loginStudent: (
    student: Student,
    sessionId: string,
    coinBalance?: number,
    isNewStudent?: boolean,
  ) => void;
  logoutStudent: (extra: { source?: string }) => void;
  setModuleCompletionThreshold: (moduleCompletionThreshold: number) => void;
  setLastActiveMission: (mission: MissionLo | undefined) => void;
  setDesktopConfig: (desktopConfig: DesktopConfig) => void;
  setAppVersion: (appVersion: string) => void;
  setRemoteConfigObject: (remoteConfigObject: Record<string, string>) => void;
  setFcmToken: (token: string) => void;
  setCallGetServerTime: (callGetServerTime: boolean) => void;
  setIsUserInitialized: (isInitialized: boolean) => void;
  setStageList: (stageList: Stage[] | null) => void;
  // setIsNarrationActive: (isNarrationActive: boolean) => void;
  setAppLanguage: (language: string) => void;
  setCurrentAdminSession: (session: { sessionId: string; adminId: string }) => void;
  clearCurrentAdminSession: () => void;
  setHasLeveledUp: (hasLeveledUp: boolean) => void;
  setHasLeveledDown: (hasLeveledDown: boolean) => void;
  setHasCompletedBaseline: (hasCompletedBaseline: boolean) => void;
  setStudentInfo: (studentInfo: StudentInfoResponse | undefined) => void;
  setShowMissionUnlockDialog: (show: boolean) => void;
  setShowMonsterCardDialog: (show: boolean) => void;
  setActivityEnabled: (activityEnabled: boolean) => void;
  setCoinsAvailable: (coinsAvailable: boolean) => void;
  setNudgesEnabled: (nudgesEnabled: boolean) => void;
  setShowSkillsCard: (show: boolean) => void;
  setPendingCoinAnimation: (amount: number) => void;
  clearPendingCoinAnimation: () => void;
}
const getCurrentStudent = () => {
  let studentData;
  const studentId = getSessionItem('studentId');
  const sessionId = getSessionItem('sessionId');
  if (studentId && sessionId) {
    studentData = getSessionItem('currentStudent') as Student;
  }
  return studentData;
};

const getCurrentAdminSession = () => {
  let adminSessionData;
  const adminSessionId = getSessionItem('adminSessionId');
  const adminId = localStorage.getItem('adminId');
  if (adminSessionId && adminId) {
    adminSessionData = { adminId, adminSessionId };
  }
  return adminSessionData as { sessionId: string; adminId: string } | undefined;
};

export const createAppSlice: StateCreator<AppState, [['zustand/devtools', never]]> = (
  set,
  get,
) => ({
  currentTheme: resolveInitialTheme(),
  loaderCount: 0,
  logoutSource: undefined,
  questionStatusArray: null,
  isInternetIssue: false,
  currentLevel: null,
  currentMission: undefined,
  currentStage: undefined,
  serverTime: undefined,
  performanceTimeReference: undefined,
  currentStudent: getCurrentStudent(),
  performanceTimeoriginReference: undefined,
  moduleCompletionThreshold: undefined,
  lastActiveMission: undefined,
  desktopConfig: null,
  appVersion: '',
  remoteConfigObject: {},
  fcmToken: undefined,
  callGetServerTime: true,
  stageList: null,
  // isNarrationActive: false,
  hasLeveledUp: false,
  hasLeveledDown: false,
  hasCompletedBaseline: false,
  showMissionUnlockDialog: false,
  showMonsterCardDialog: false,
  activityEnabled: undefined,
  coinsAvailable: undefined,
  nudgesEnabled: undefined,
  showSkillsCard: false,
  pendingCoinAnimation: 0,
  isNewStudent: undefined,
  isUserInitialized: false,
  appLanguage: 'en',
  currentAdminSession: getCurrentAdminSession(),
  studentInfo: undefined,
  setCurrentTheme: (theme) => set({ currentTheme: theme }, false, 'appSlice/setCurrentTheme'),
  setShowLoader: (showLoader) =>
    set(
      (state) => ({
        loaderCount: showLoader ? state.loaderCount + 1 : state.loaderCount - 1,
      }),
      false,
      'appSlice/setShowLoader',
    ),

  setIsNewStudent: (isNewStudent) => set({ isNewStudent }, false, 'appSlice/setIsNewStudent'),

  setQuestionStatusArray: (questionStatusArray) =>
    set({ questionStatusArray }, false, 'appSlice/setQuestionStatusArray'),

  setInternetIssue: (isInternetIssue) =>
    set({ isInternetIssue }, false, 'appSlice/setInternetIssue'),

  setCurrentLevel: (level) => set({ currentLevel: level }, false, 'appSlice/setCurrentLevel'),

  setCurrentMission: (mission) =>
    set({ currentMission: mission }, false, 'appSlice/setCurrentMission'),

  setCurrentStage: (stage) => set({ currentStage: stage }, false, 'appSlice/setCurrentStage'),
  setServerTime: (serverTime) => {
    const {
      getCurrentTime,
      performanceReference,
      performanceTimeoriginReference,
      serverTime: previousServerTime,
    } = get();
    const currentTime = getCurrentTime();
    const previousPerformanceNow = performanceReference;
    const previousPerformanceTimeorigin = performanceTimeoriginReference;

    set(
      {
        serverTime,
        performanceReference: performance.now(),
        performanceTimeoriginReference: performance.timeOrigin,
      },
      false,
      'appSlice/setServerTime',
    );
    const newTime = getCurrentTime();
    const diff = newTime - currentTime;
    const serverTimeMs = new Date(serverTime).getTime();
    const previousServerTimeMs = previousServerTime ? new Date(previousServerTime).getTime() : 0;
    if (Math.abs(diff) > 60 * 1000 && serverTimeMs - previousServerTimeMs < 60 * 1000) {
      logger.error('Time difference is too big', diff, newTime, currentTime);
      logger.info(
        'performance.now() and performanceReference and diff',
        performance.now(),
        previousPerformanceNow,
        performance.now() - (previousPerformanceNow ?? 0),
      );

      logger.info(
        'performance.timeOrigin and performanceTimeoriginReference and diff',
        performance.timeOrigin,
        previousPerformanceTimeorigin,
        performance.timeOrigin - (previousPerformanceTimeorigin ?? 0),
      );
    }
  },
  getCurrentTime: () => {
    const { serverTime, performanceReference, performanceTimeoriginReference } = get();
    const result =
      serverTime && performanceReference && performanceTimeoriginReference
        ? new Date(serverTime).getTime() +
          Math.floor(performance.now() - performanceReference) +
          Math.floor(performance.timeOrigin - performanceTimeoriginReference)
        : Date.now();
    // store.setState((state) => state, false, `appSlice/${result}`);
    return result;
  },
  loginStudent: (student, sessionId, coinBalance = 0, isNewStudent?) => {
    set(
      { currentStudent: student, logoutSource: undefined, isUserInitialized: false, isNewStudent },
      false,
      'appSlice/loginStudent',
    );
    setSessionItem('currentStudent', student);
    setSessionItem('studentId', student.id);
    setSessionItem('sessionId', sessionId);
    setSessionItem(COIN_BALANCE_SESSION_KEY, coinBalance);
  },

  logoutStudent: ({ source }) => {
    teardownNudging();
    set(
      {
        currentStudent: undefined,
        logoutSource: source,
        isUserInitialized: false,
        isNewStudent: undefined,
      },
      false,
      'appSlice/setCurrentStudent',
    );
    removeSessionItem('currentStudent');
    removeSessionItem('studentId');
    removeSessionItem('sessionId');
    removeSessionItem(COIN_BALANCE_SESSION_KEY);
    void window.electronAPI?.closeSsoWindow?.();
  },

  setModuleCompletionThreshold: (moduleCompletionThreshold) =>
    set({ moduleCompletionThreshold }, false, 'appSlice/setModuleCompletionThreshold'),

  setLastActiveMission: (mission) =>
    set({ lastActiveMission: mission }, false, 'appSlice/setLastActiveMission'),

  setDesktopConfig: (desktopConfig) => set({ desktopConfig }, false, 'appSlice/setDesktopConfig'),
  setAppVersion: (appVersion) => set({ appVersion }, false, 'appSlice/setAppVersion'),
  setRemoteConfigObject: (remoteConfigObject) =>
    set({ remoteConfigObject }, false, 'appSlice/setRemoteConfigObject'),

  setFcmToken: (token) => set({ fcmToken: token }, false, 'appSlice/setFcmToken'),
  setCallGetServerTime: (callGetServerTime) =>
    set({ callGetServerTime }, false, 'appSlice/setCallGetServerTime'),

  setIsUserInitialized: (isInitialized) =>
    set({ isUserInitialized: isInitialized }, false, 'appSlice/setIsUserInitialized'),

  setStageList: (stageList) => set({ stageList }, false, 'appSlice/setStageList'),

  // setIsNarrationActive: (isNarrationActive) =>
  //   set({ isNarrationActive }, false, 'appSlice/setIsNarrationActive'),

  setAppLanguage: (language) => set({ appLanguage: language }, false, 'appSlice/setAppLanguage'),

  setCurrentAdminSession: ({ sessionId, adminId }) => {
    set({ currentAdminSession: { sessionId, adminId } }, false, 'appSlice/setCurrentAdminSession');
    setSessionItem('adminSessionId', sessionId);
  },

  clearCurrentAdminSession: () => {
    set({ currentAdminSession: undefined }, false, 'appSlice/clearCurrentAdminSession');
    removeSessionItem('adminSessionId');
  },
  setHasLeveledUp: (hasLeveledUp) => set({ hasLeveledUp }, false, 'appSlice/setHasLeveledUp'),
  setHasLeveledDown: (hasLeveledDown) =>
    set({ hasLeveledDown }, false, 'appSlice/setHasLeveledDown'),
  setHasCompletedBaseline: (hasCompletedBaseline) =>
    set({ hasCompletedBaseline }, false, 'appSlice/setHasCompletedBaseline'),
  setStudentInfo: (studentInfo) => set({ studentInfo }, false, 'appSlice/setStudentInfo'),

  setShowMissionUnlockDialog: (show) =>
    set({ showMissionUnlockDialog: show }, false, 'appSlice/setShowMissionUnlockDialog'),

  setShowMonsterCardDialog: (show) =>
    set({ showMonsterCardDialog: show }, false, 'appSlice/setShowMonsterCardDialog'),

  setActivityEnabled: (activityEnabled) =>
    set({ activityEnabled }, false, 'appSlice/setActivityEnabled'),

  setCoinsAvailable: (coinsAvailable) =>
    set({ coinsAvailable }, false, 'appSlice/setCoinsAvailable'),
  setNudgesEnabled: (nudgesEnabled) => {
    set({ nudgesEnabled }, false, 'appSlice/setNudgesEnabled');
    // Stop a running engine if nudges get turned off; the engine itself reads the flag live.
    if (!nudgesEnabled) teardownNudging();
  },
  setShowSkillsCard: (show) => set({ showSkillsCard: show }, false, 'appSlice/setShowSkillsCard'),
  setPendingCoinAnimation: (amount) =>
    set({ pendingCoinAnimation: amount }, false, 'appSlice/setPendingCoinAnimation'),
  clearPendingCoinAnimation: () =>
    set({ pendingCoinAnimation: 0 }, false, 'appSlice/clearPendingCoinAnimation'),
});
